"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
const DeferredPromiseExecutor_1 = require("../classes/DeferredPromiseExecutor");
const index_1 = require("../index");
const DMSMethod_1 = require("../enums/DMSMethod");
const os = require("os");
const DMSNodeState_1 = require("../enums/DMSNodeState");
const WebSocket = require("isomorphic-ws");
class DMSWSClient {
    //endregion
    //region Constructor
    constructor(dmsHost, dmsPort, useWss, clientName, clientVersion, delegate) {
        var _a, _b;
        this.runtime = ((_b = (_a = globalThis.process) === null || _a === void 0 ? void 0 : _a.release) === null || _b === void 0 ? void 0 : _b.name) || "browser";
        //endregion
        //region Public Methods
        this.connect = (jwtToken) => {
            if (DMSWSClient.isBanned) {
                return new Promise((resolve, reject) => {
                    setTimeout(() => {
                        this.delegate.onConnectionFailed(this, 0, "banned");
                        resolve(false);
                    }, 1000);
                });
            }
            const connectionTimeout = 7500;
            let dpe = new DeferredPromiseExecutor_1.default(connectionTimeout);
            // the deferred id which is unique to this scope.
            const deferredId = DeferredPromiseExecutor_1.default.generateRandomId();
            let deferredPromise = undefined;
            try {
                deferredPromise = dpe.defer(deferredId);
                this.jwtToken = jwtToken;
                const wsScheme = this.useWss ? "wss" : "ws";
                const wsUrl = `${wsScheme}://${this.dmsHost}:${this.dmsPort}?token=${this.jwtToken}`;
                console.log(`${this.clientName} connecting to ${this.dmsHost}:${this.dmsPort}`);
                if (this.websocket) {
                    this.websocket.removeAllListeners();
                    this.websocket = undefined;
                }
                const isRunningInNode = this.runtime !== "browser";
                this.websocket = new WebSocket(wsUrl);
                let isConnected = false;
                if (isRunningInNode) {
                    this.websocket.on("unexpected-response", (request, response) => {
                        if (response.statusCode === 401) {
                            console.log("auth failed");
                            if (dpe) {
                                dpe.reject(deferredId, new Error("auth failed"));
                                dpe = undefined;
                            }
                        }
                        else if (response.statusCode === 403) {
                            console.warn("the client was banned");
                            DMSWSClient.isBanned = true;
                            if (dpe) {
                                dpe.reject(deferredId, new Error("banned"));
                                dpe = undefined;
                            }
                        }
                    });
                }
                this.websocket.onerror = (event) => {
                    console.error("internal error:", event.error);
                    this.dmsMessageHandler.removeAllCallables();
                    if (dpe) {
                        dpe.reject(deferredId, event.error);
                        dpe = undefined;
                    }
                };
                this.websocket.onopen = (event) => {
                    isConnected = true;
                    if (dpe) {
                        dpe.resolve(deferredId, true);
                        dpe = undefined;
                    }
                    this.delegate.onConnectedToDms(this);
                    if (isRunningInNode) {
                        this.heartbeat();
                    }
                };
                if (isRunningInNode) {
                    this.websocket.on("ping", (p) => this.heartbeat(p));
                }
                this.websocket.onclose = (event) => {
                    if (isRunningInNode && this.heartbeatTimeout) {
                        clearTimeout(this.heartbeatTimeout);
                    }
                    if (dpe) {
                        dpe.resolve(deferredId, false);
                        dpe = undefined;
                    }
                    if (!isConnected) {
                        // was never connected in the first place
                        this.delegate.onConnectionFailed(this, event.code, event.reason);
                    }
                    else {
                        this.dmsMessageHandler.removeAllCallables();
                        this.delegate.onDisconnectedFromDms(this, event.code, event.reason);
                    }
                };
                this.websocket.onmessage = (e) => {
                    this.onDmsMessage(e.data);
                };
            }
            catch (error) {
                dpe.reject(deferredId, error);
            }
            return deferredPromise;
        };
        this.disconnect = () => {
            var _a;
            if (this.websocket) {
                (_a = this.websocket) === null || _a === void 0 ? void 0 : _a.close();
                this.websocket = undefined;
            }
        };
        this.sendMessage = (msg, awaitingResult) => __awaiter(this, void 0, void 0, function* () {
            return this.dmsMessageHandler.send(msg, this.websocket, awaitingResult);
        });
        this.sendNotification = (msg) => {
            var _a;
            const msgJson = JSON.stringify(msg);
            (_a = this.websocket) === null || _a === void 0 ? void 0 : _a.send(msgJson);
        };
        this.sendData = (data, onCompleted) => {
            var _a;
            (_a = this.websocket) === null || _a === void 0 ? void 0 : _a.send(data, (err) => {
                if (onCompleted) {
                    onCompleted(err);
                }
                if (err) {
                    console.error("error when sending data: ", err);
                }
            });
        };
        this.registerCallable = (method, callable) => {
            this.dmsMessageHandler.registerCallable(method, callable);
        };
        this.logState = (nodeState, requestingUser, data) => __awaiter(this, void 0, void 0, function* () {
            var _c;
            if (!this.jwtToken) {
                throw new Error("state can only be logged when the node is authenticated!");
            }
            try {
                const logEntry = {
                    state: nodeState,
                    user: requestingUser,
                    data
                };
                const logMessage = index_1.DMSMessageFactory.newMessage(index_1.DMSMethod.WRITE_NODE_LOG_ENTRY, logEntry);
                const res = yield this.sendMessage(logMessage, true);
                return (_c = res === null || res === void 0 ? void 0 : res.result) !== null && _c !== void 0 ? _c : false;
            }
            catch (e) {
                console.warn("failed to log state, Error: ", e);
            }
            return false;
        });
        this.resetState = (requestingUser, data) => {
            return this.logState(DMSNodeState_1.default.NODE_STATE_IDLE, requestingUser, data);
        };
        //endregion
        //region Private Methods
        this.heartbeat = (pingPayload) => {
            if (this.runtime === "browser") {
                console.warn("heartbeat is not supported when running in the browser!");
            }
            if (this.heartbeatTimeout) {
                clearTimeout(this.heartbeatTimeout);
            }
            // Delay should be equal to the interval at which dms
            // sends out pings plus a conservative assumption of the latency.
            this.heartbeatTimeout = setTimeout(() => {
                var _a, _b;
                console.warn("terminating the connection to DMS due to inactivity");
                if ((_a = this.websocket) === null || _a === void 0 ? void 0 : _a.terminate) {
                    (_b = this.websocket) === null || _b === void 0 ? void 0 : _b.terminate();
                }
            }, index_1.DMSConstants.DMS_PING_INTERVAL + 1000);
        };
        this.onDmsMessage = (data) => __awaiter(this, void 0, void 0, function* () {
            const typeOfData = typeof data;
            if (typeOfData === "string") {
                try {
                    const msg = this.dmsMessageHandler.deserialize(data.toString());
                    yield this.dmsMessageHandler.processIncomingMessage(msg, this.websocket);
                }
                catch (error) {
                    console.error("unable to process the incoming message: ", error);
                }
            }
            else if (typeOfData === "object") {
                if (this.delegate.onData) {
                    this.delegate.onData(this, data);
                }
            }
            else {
                throw new Error(typeOfData + " messages are not supported");
            }
        });
        //endregion
        //region Private Methods
        this.setupHandler = () => {
            const handler = new index_1.DMSMessageHandler();
            handler.registerCallable(DMSMethod_1.ClientNotifications.CONNECTION_ACK, {
                implementation: this.onConnectionAcknowledged,
                parameters: [
                    { type: "string", name: "motd" },
                    { type: "object", name: "version" },
                    { type: "string", name: "uid" }
                ]
            });
            return handler;
        };
        this.onConnectionAcknowledged = (message) => __awaiter(this, void 0, void 0, function* () {
            var _d;
            const info = yield ((_d = this.delegate) === null || _d === void 0 ? void 0 : _d.onConnectionAcknowledged(this, message.payload));
            if (info) {
                return info;
            }
            const osType = os.type();
            let osString = "browser";
            if (osType !== "Browser") {
                osString = os.type() + " " + os.release();
            }
            return { operatingSystem: osString, clientName: this.clientName, clientVersion: this.clientVersion };
        });
        if (delegate == null) {
            throw new Error("A delegate is required");
        }
        this.dmsHost = dmsHost;
        this.dmsPort = dmsPort;
        this.useWss = useWss;
        this.clientName = clientName;
        this.clientVersion = clientVersion;
        this.delegate = delegate;
        this.dmsMessageHandler = this.setupHandler();
    }
}
exports.DMSWSClient = DMSWSClient;
//endregion
//region Public Static Fields
DMSWSClient.isBanned = false;
//# sourceMappingURL=DMSWSClient.js.map